import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Checkbox, Dropdown, Header, Icon, Segment } from 'semantic-ui-react'
import {
  ADMIN_ROLE_SLUG,
  MILK_SAMPLES_FILTER_KEY,
  OPERATIVE_ROLE_SLUG,
  milkSamplesFilters,
  milkSamplesIDs
} from '~/Constants'
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import i18n from '~/components/i18n'
import TableComponent from '~/components/TableComponent'
import { El } from '~/components/Layout/style'
import Dialog from '~/components/Dialog'
import { StyledFileLabel, StyledFileWrapper } from '~/pages/Dairy/MilkFixedPrice/style'
import FileUpload from '~/components/FileUpload'
import axios from 'axios'
import ENV from '~/environment';
import { RouteTypes } from '~/routes/constants'
import { useAuth } from '~/routes/components/AuthProvider'
import Filters from './Filters'
import { StyledFiltersOptions } from '../style'
import { ThemeContext } from 'styled-components'

const getDefaultTableComunHeaders = () => [
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo',
    sorted: 'desc'
  },
  {
    name: i18n.t('Procurement month'),
    key: 'billingMonth',
    type: 'billingMonth'
  },
  {
    name: i18n.t('Date'),
    key: 'date',
    type: 'date',
    format: 'DD.MM.YYYY',
    sorted: 'desc'
  },
  {
    name: i18n.t('Fat (in %)'),
    key: 'fat',
    type: 'no-unit-number',
    sorted: 'desc'
  },
  {
    name: i18n.t('Protein (in %)'),
    key: 'protein',
    type: 'no-unit-number',
    sorted: 'desc'
  },
  {
    name: i18n.t('Germ count (1000/ml)'),
    key: 'germCount',
    type: 'no-unit-number',
    decimalScale: 0,
    sorted: 'desc'
  },
  {
    name: i18n.t('Cell count (1000/ml)'),
    key: 'cellCount',
    type: 'no-unit-number',
    decimalScale: 0,
    sorted: 'desc'
  },
  {
    name: i18n.t('Inhibitor'),
    key: 'inhibitorLabel'
  },
  {
    name: i18n.t('Freezing point (in °C)'),
    key: 'freezingPoint',
    type: 'no-unit-number-no-value',
    sorted: 'desc'
  },
  {
    name: i18n.t('Urea (mg/dl)'),
    key: 'urea',
    type: 'no-unit-number-no-value',
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
]

const getPseudomTableComunHeaders = () => [
  {
    name: i18n.t('Procurement month'),
    key: 'date',
    type: 'date',
    format: 'MMM YYYY'
  },
  {
    name: i18n.t('Supplier No.'),
    key: 'supplierNo'
  },

  {
    name: i18n.t('Status level (current month)'),
    key: 'levelStatus'
  },
  {
    name: i18n.t('Status level (last month)'),
    key: 'levelStatusLastMonth'
  },
  {
    name: i18n.t('Actions')
  }
]

const defaultDataFilter = {
  page: 0,
  rows: 20,
  search: '',
  column: '',
  sort: '',
  fatFrom: '',
  fatTo: '',
  proteinFrom: '',
  proteinTo: '',
  date: '',
  germCountFrom: '',
  germCountTo: '',
  cellCountFrom: '',
  cellCountTo: '',
  inhibitor: '',
  ureaFrom: '',
  ureaTo: ''
}

const MilkSamples: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { firstColor } = useContext(ThemeContext)
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const [checkedFilters, setCheckedFilters] = useState([])
  const [openModal, setOpenModal] = useState(false)
  const [loadImportingFile, setLoadImportingFile] = useState(false)
  const [dataFiltered, setDataFiltered] = useState(defaultDataFilter)
  const displayManualImport = roleSlug === ADMIN_ROLE_SLUG || roleSlug === OPERATIVE_ROLE_SLUG
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableComunHeaders())
  const [file, setFile] = useState(null)
  const [urlId, setUrlId] = useState(id)
  const actions = [
    {
      popupMessage: i18n.t('Show more'),
      type: 'link',
      name: 'eye',
      size: 'large',
      link: RouteTypes.MILK_MONEY_SAMPLES_SHOW.replace(':sampleType', id)
    }
  ]

  useEffect(() => {
    setUrlId(id)
    setDataFiltered(defaultDataFilter)
  }, [id])

  useEffect(() => {
    fetchOptions()
    const filters = localStorage.getItem(MILK_SAMPLES_FILTER_KEY)
    if (filters) {
      setCheckedFilters(JSON.parse(filters).filter(filter => filter?.filterDisplayIds?.find(idFilterDisplay => idFilterDisplay === urlId)))
    }
    setTableColumnHeaders(getDefaultTableComunHeaders())
  }, [t, urlId])

  useEffect(() => {
    const filters = localStorage.getItem(MILK_SAMPLES_FILTER_KEY)
    if (filters) {
      setCheckedFilters(JSON.parse(filters))
    }
  }, [])

  const {
    isLoading: isBusyData,
    data: milkSamplesData,
    refetch: fetchFilteredData,
    isFetchedAfterMount: isFetchedAfterMount,
    isFetching: isFetchingData
  } = MilkMoneyQuery.getMilkSamplesData(id, id === urlId ? {
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  } : defaultDataFilter);

  const {
    isLoading: isBusyOptions,
    data: milkSamplesOptions,
    refetch: fetchOptions
  } = MilkMoneyQuery.getMilkSamplesFilterData(id)

  useEffect(() => {
    fetchFilteredData()
  }, [])

  useEffect(() => {
    if (isFetchedAfterMount) {
      fetchFilteredData()
    }
  }, [dataFiltered]);

  function renderFilters() {
    return <Filters
      dataFiltered={dataFiltered}
      id={id}
      handleChangeFiltersData={(data) => setDataFiltered(data)}
      options={milkSamplesOptions}
      checkedFilters={checkedFilters}
    />
  }

  function changeKeyColumnMonth(key, changeKey) {
    const columns = [...tableColumnHeaders]
    const index = columns.findIndex(column => column.key === changeKey)
    if (index >= 0) {
      columns[index].key = key
    }
    return columns
  }

  function getFilteredTableColumns() {
    const dairyRemovedColumns = ['date', 'supplierNo', 'inhibitorLabel']
    //refactor these filters
    switch (id) {
      case milkSamplesIDs.allIndividual:
        return changeKeyColumnMonth('samplingDate', 'billingMonth')
      case milkSamplesIDs.farmer:
        return changeKeyColumnMonth('billingMonth', 'samplingDate').filter(column => column.type !== 'date').filter(column => column.key !== 'freezingPoint')
      case milkSamplesIDs.dairy:
        return changeKeyColumnMonth('billingMonth', 'samplingDate').filter(column => !dairyRemovedColumns.find(key => column.key === key))
      case milkSamplesIDs.dailyValues:
        return changeKeyColumnMonth('samplingDate', 'billingMonth').filter(column => column.key !== 'inhibitorLabel')
      case milkSamplesIDs.pseudom:
        return getPseudomTableComunHeaders()
      default:
        return []
    }
  }

  function renderTable() {
    const filteredTableColumns = getFilteredTableColumns()
    return <TableComponent
      actions={actions}
      isFetchingData={isFetchingData}
      animated
      tableColumnHeaders={filteredTableColumns}
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
      rows={rows}
      total={filtered}
    />
  }

  function getTitle() {
    switch (id) {
      case milkSamplesIDs.allIndividual:
        return t('All individual values')
      case milkSamplesIDs.farmer:
        return t('Farmers average values')
      case milkSamplesIDs.dairy:
        return t('Dairy average values')
      case milkSamplesIDs.dailyValues:
        return t('Daily samples')
      case milkSamplesIDs.pseudom:
        return t('Pseudom.')
      default:
        return ''
    }
  }

  function renderActions() {
    return <>
      <StyledFiltersOptions
        text={t('Filters')}
        icon='filter'
        floating
        labeled
        button
        closeOnChange={false}
        multiple
        className='icon'
        style={{ background: firstColor, color: 'white' }}
      >
        <Dropdown.Menu>
          <Dropdown.Menu scrolling>
            {milkSamplesFilters.map(filter => {
              const displayFilter = filter.filterDisplayIds.find(idFilterDisplay => idFilterDisplay === urlId)
              const removeItem = id === milkSamplesIDs.dailyValues && filter.key === 'inhibitor'
              return !removeItem && displayFilter && < Dropdown.Item
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                key={filter.key}
              >
                <Checkbox
                  onClick={(e, data) => {
                    e.stopPropagation()
                    e.preventDefault()
                    if (data.checked) {
                      setCheckedFilters([...checkedFilters, filter])
                      localStorage.setItem(MILK_SAMPLES_FILTER_KEY, JSON.stringify([...checkedFilters, filter]))
                    } else {
                      setCheckedFilters(checkedFilters.filter(filterChecked => filterChecked.key !== filter.key))
                      localStorage.setItem(MILK_SAMPLES_FILTER_KEY, JSON.stringify(checkedFilters.filter(filterChecked => filterChecked.key !== filter.key)))
                    }
                  }}
                  checked={Boolean(checkedFilters.find(filterChecked => filterChecked.key === filter.key))}
                  label={t(filter.placeholder)}
                />
              </Dropdown.Item>
            })}
          </Dropdown.Menu>
        </Dropdown.Menu>
      </StyledFiltersOptions >
      {displayManualImport && <El.PageHeaderActionButton
        size='large'
        onClick={() => setOpenModal(true)}
      >
        {t('Manual import')}
      </El.PageHeaderActionButton>
      }
    </>
  }

  function renderFile() {
    return <StyledFileWrapper>
      <StyledFileLabel
        basic
        color='blue'
      >
        <Icon name='file' />
        <div className='fileName'>{file?.name}</div>
      </StyledFileLabel>
    </StyledFileWrapper>
  }

  function renderImportForm() {
    const pseudomSamplePage = milkSamplesIDs.pseudom === id
    return <>
      <Header style={{ position: 'relative', minWidth: 230, width: '50%' }} attached='top' block>{t('File upload')}</Header>
      <Segment
        loading={loadImportingFile}
        attached
        style={{ position: 'relative', minWidth: 230, width: '50%' }}
      >
        {Boolean(file) && renderFile()}
        <FileUpload
          type='button'
          mimeTypes={pseudomSamplePage ? ['csv', 'txt'] : ['ADIS']}
          sizeLimitInMB={10}
          onChange={(file) => {
            if (file) {
              setFile(file)
            }
          }}
        />
      </Segment>
    </>
  }

  async function handleUploadFile(data) {
    setLoadImportingFile(true)
    try {
      const formData = new FormData()
      formData.append('file', data.file)
      const url = `/milk-money/milk-samples/${id}/import`
      await axios.post(`${ENV.API_URL}${url}`, formData, {
        headers: { 'Authorization': localStorage.getItem('token') }
      });
      setFile(null)
      setOpenModal(false)
      notify(t('Successfully imported file'), 'success')
      fetchFilteredData()
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
    setLoadImportingFile(false)
  }

  function renderImportModal() {
    return <Dialog
      isDialogOpen={openModal}
      textHeader={t('Milk samples - {{name}} Import', { name: getTitle() })}
      textClose={t('Cancel')}
      textSubmit={t('Import')}
      handleClose={() => {
        setFile(null)
        setOpenModal(false)
      }}
      isLoading={loadImportingFile}
      disableSubmit={!file}
      handleSubmit={() => handleUploadFile({ file: file })}
      textContent={<>
        {renderImportForm()}
      </>}
    />
  }

  const { filtered, rows } = milkSamplesData ? milkSamplesData : { filtered: 0, rows: [] };

  return <Layout
    segmented
    isBusy={isBusyData || isBusyOptions}
    title={getTitle()}
    filters={milkSamplesOptions && renderFilters()}
    actions={renderActions()}
  >
    {openModal && renderImportModal()}
    {renderTable()}
  </Layout>
}
export default MilkSamples